import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';

function createEmptyValues(fields) {
  return fields.reduce((accumulator, field) => {
    accumulator[field.name] = '';
    return accumulator;
  }, {});
}

function renderValue(value) {
  if (value === '' || value === null || value === undefined) {
    return '-';
  }

  return String(value);
}

export default function AdminCrudPage({
  label,
  singular,
  description,
  note,
  stats,
  fields,
  initialItems,
}) {
  const [items, setItems] = useState(initialItems);
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState(() => createEmptyValues(fields));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const visibleColumns = useMemo(() => {
    if (isMobile) {
      return fields.slice(0, 2);
    }

    if (isTablet) {
      return fields.slice(0, 4);
    }

    return fields.slice(0, 6);
  }, [fields, isMobile, isTablet]);
  const filledCount = items.length;
  const activeCount = items.filter(
    (item) =>
      item.status === '1' ||
      item.status === 'ready' ||
      item.is_read === '1'
  ).length;

  const headerMetrics = [
    { label: `${label} total`, value: String(filledCount) },
    { label: 'Tracked fields', value: String(fields.length) },
    { label: 'Active / flagged', value: String(activeCount) },
  ];

  const handleChange = (fieldName, value) => {
    setFormValues((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const resetForm = () => {
    setFormValues(createEmptyValues(fields));
    setEditingId(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (editingId !== null) {
      setItems((current) =>
        current.map((item) =>
          item.id === editingId ? { ...item, ...formValues } : item
        )
      );
      resetForm();
      return;
    }

    const nextId =
      items.length > 0 ? Math.max(...items.map((item) => Number(item.id))) + 1 : 1;

    setItems((current) => [
      ...current,
      {
        id: nextId,
        ...formValues,
      },
    ]);
    resetForm();
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormValues(
      fields.reduce((accumulator, field) => {
        accumulator[field.name] = item[field.name] ?? '';
        return accumulator;
      }, {})
    );
  };

  const handleDelete = (itemId) => {
    setItems((current) => current.filter((item) => item.id !== itemId));
    if (editingId === itemId) {
      resetForm();
    }
  };

  return (
    <>
      <Grid container spacing={2.5} className='admin-dashboard-metrics'>
        {headerMetrics.map((metric) => (
          <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
            <Paper className='admin-dashboard-metricCard' elevation={0}>
              <Typography className='admin-dashboard-metricLabel'>
                {metric.label}
              </Typography>
              <Typography className='admin-dashboard-metricValue'>
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <Paper className='admin-dashboard-featureCard admin-crud-formCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  {editingId ? 'Edit record' : 'Create record'}
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {editingId ? `Update ${singular}` : `Add ${singular}`}
                </Typography>
              </Box>
              <Chip
                label={editingId ? 'Edit mode' : 'New entry'}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-cardText'>
              {description}
            </Typography>

            <Box component='form' onSubmit={handleSubmit} className='admin-crud-form'>
              <Grid container spacing={2}>
                {fields.map((field) => (
                  <Grid
                    key={field.name}
                    size={{
                      xs: 12,
                      md: field.type === 'textarea' ? 12 : 6,
                    }}
                  >
                    <TextField
                      fullWidth
                      select={field.type === 'select'}
                      multiline={field.type === 'textarea'}
                      minRows={field.type === 'textarea' ? 4 : undefined}
                      type={
                        field.type === 'textarea' || field.type === 'select'
                          ? 'text'
                          : field.type
                      }
                      label={field.label}
                      required={field.required}
                      value={formValues[field.name]}
                      onChange={(event) =>
                        handleChange(field.name, event.target.value)
                      }
                    >
                      {field.type === 'select'
                        ? field.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))
                        : null}
                    </TextField>
                  </Grid>
                ))}
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                className='admin-crud-actions'
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={editingId ? <SaveRoundedIcon /> : <AddRoundedIcon />}
                  className='admin-crud-primaryButton'
                >
                  {editingId ? `Update ${singular}` : `Add ${singular}`}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetForm}
                  className='admin-crud-secondaryButton'
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Paper className='admin-dashboard-sideCard admin-crud-tableCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  Records
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {label} list
                </Typography>
              </Box>
              <Chip
                label={`${items.length} rows`}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-note'>{note}</Typography>
            <Divider className='admin-dashboard-divider' />

            <TableContainer className='admin-crud-tableWrap'>
              <Table className='admin-crud-table'>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    {visibleColumns.map((field) => (
                      <TableCell key={field.name}>{field.label}</TableCell>
                    ))}
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      {visibleColumns.map((field) => (
                        <TableCell key={field.name}>
                          {renderValue(item[field.name])}
                        </TableCell>
                      ))}
                      <TableCell align='right'>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          justifyContent='flex-end'
                          className='admin-crud-rowActions'
                        >
                          <Button
                            size='small'
                            variant='text'
                            startIcon={<EditRoundedIcon />}
                            onClick={() => handleEdit(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            size='small'
                            color='error'
                            variant='text'
                            startIcon={<DeleteOutlineRoundedIcon />}
                            onClick={() => handleDelete(item.id)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          No records yet. Use the form to create the first {singular.toLowerCase()}.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {stats?.length ? (
        <Box className='admin-dashboard-pillGrid'>
          {stats.map((item) => (
            <Box key={item} className='admin-dashboard-pill'>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
}
